import React, { useEffect, useState } from "react";
import { Box, Text, Spinner, VStack, Badge, Button } from "@chakra-ui/react";
import { collection, getDocs, query, where, orderBy, doc, getDoc } from "firebase/firestore";
import { firestore } from "../firebase/firebaseConfig";
import { useNavigate } from "react-router-dom";
import { REWARD_TIERS } from "../utils/rewards"; // Import standardized rewards

const DashboardLeaderboard = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTopPerformers = async () => {
      try {
        const userClicks = {}; // Store aggregated verified clicks per user

        // 🔥 Query all shares that have verified clicks
        const sharesQuery = query(collection(firestore, "user_shares"), where("verifiedClicks", ">", 0));
        const querySnapshot = await getDocs(sharesQuery);

        querySnapshot.docs.forEach((doc) => {
          const { userId, verifiedClicks } = doc.data();
          if (!userClicks[userId]) {
            userClicks[userId] = 0;
          }
          userClicks[userId] += verifiedClicks;
        });

        // 🔥 Convert into sorted leaderboard (Top 3)
        const sortedLeaderboard = Object.entries(userClicks)
          .map(([userId, verifiedClicks]) => ({ id: userId, verifiedClicks }))
          .sort((a, b) => b.verifiedClicks - a.verifiedClicks)
          .slice(0, 3); // ✅ Limit to top 3 performers

        // 🔥 Fetch user details for leaderboard display
        const enrichedLeaderboard = await Promise.all(
          sortedLeaderboard.map(async (user, index) => {
            const userRef = doc(firestore, "users", user.id);
            const userSnap = await getDoc(userRef);
            const userData = userSnap.exists() ? userSnap.data() : {};

            return {
              id: user.id,
              rank: index + 1,
              name: userData.userName || "Anonymous",
              verifiedClicks: user.verifiedClicks,
            };
          })
        );

        setLeaderboard(enrichedLeaderboard);
      } catch (error) {
        console.error("❌ Error fetching leaderboard:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTopPerformers();
  }, []);

  return (
    <Box>
      {loading ? (
        <Box textAlign="center">
          <Spinner size="lg" />
          <Text mt={2} color="gray.500">Loading leaderboard...</Text>
        </Box>
      ) : leaderboard.length === 0 ? (
        <Text color="gray.500">No leaderboard data available.</Text>
      ) : (
        <>
          <VStack spacing={4} align="stretch">
            {leaderboard.map((user) => {
              // 🎯 Find the highest milestone reward the user has achieved
              const highestMilestone = Object.keys(REWARD_TIERS)
                .reverse()
                .find((threshold) => user.verifiedClicks >= threshold);

              return (
                <Box
                  key={user.id}
                  p={4}
                  bg="gray.100"
                  borderRadius="md"
                  shadow="sm"
                  _hover={{ bg: "gray.200" }}
                >
                  <Text fontSize="md" fontWeight="bold">
                    #{user.rank} {user.name}
                  </Text>
                  <Text fontSize="sm">🔗 {user.verifiedClicks} verified clicks</Text>

                  {/* 🎯 Display the highest milestone reward achieved */}
                  {highestMilestone && (
                    <Badge mt={2} colorScheme="green">
                      🎉 {REWARD_TIERS[highestMilestone]}
                    </Badge>
                  )}
                </Box>
              );
            })}
          </VStack>

          {/* ✅ Only show "View More" if there are top performers */}
          {leaderboard.length > 0 && (
            <Button
              mt={4}
              colorScheme="blue"
              w="full"
              onClick={() => navigate("/personal/leaderboard")}
            >
              View More 🔍
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default DashboardLeaderboard;
